import type { Table } from '@tanstack/react-table'

/** Breathing room past the measured content. */
const CONTENT_SLACK = 6

/** The trailing "+" add chip a chip cell always keeps room for (`useChipOverflow`'s reservedWidth). */
const CHIP_ADD_AFFORDANCE = 30

/** Both gutters, for text measured off-DOM where cell padding isn't included. */
const TEXT_CELL_GUTTERS = 20

/** Matches the floor `calculateColumnWidths` uses, so a fit can't make a column unusable. */
const MIN_WIDTH = 60

/** A column wide enough to push every other column off-screen helps nobody. */
const MAX_WIDTH = 600

/**
 * Widths track character count closely enough within one font that measuring the
 * longest strings finds the widest one, without running the text metrics over
 * twenty thousand rows.
 */
const LONGEST_VALUES_TO_MEASURE = 100

let measuringCanvas: HTMLCanvasElement | undefined

function textWidthMeasurer(font: string): (text: string) => number {
  measuringCanvas ??= document.createElement(`canvas`)
  const context = measuringCanvas.getContext(`2d`)
  if (!context) return () => 0
  context.font = font
  return (text) => context.measureText(text).width
}

/**
 * What this element would be if nothing constrained it, borrowed from the browser's
 * own intrinsic sizing.
 *
 * Measuring laid-out positions instead does not work here: a cell's content is a
 * flex row stretched to the column, and anything right-aligned in it (a dropdown
 * caret, the header's sort chevron) sits at the column's current right edge. Reading
 * those positions reports the width the column already has, so no column could ever
 * shrink. Under `max-content` the row collapses to the sum of its items and auto
 * margins contribute nothing, which is the number we actually want.
 */
function intrinsicWidth(element: HTMLElement): number {
  const previousWidth = element.style.width
  element.style.width = `max-content`
  const width = element.getBoundingClientRect().width
  element.style.width = previousWidth
  return width
}

type CellMeasurement = {
  /** How wide this cell's content wants to be. */
  width: number
  /** The longest text in the cell, which is its value where it has one. */
  longestText: string
}

function measureCell(cell: HTMLElement): CellMeasurement {
  const cellLeft = cell.getBoundingClientRect().left
  let content = 0
  for (const child of cell.children) {
    content = Math.max(content, intrinsicWidth(child as HTMLElement))
  }

  // Chip cells (tags, pipelines, task status) render only the chips that fit plus a
  // "+N", so the visible row is never wider than the column already is. Their hidden
  // measure row is the only place the complete set exists; it is absolutely
  // positioned and already sits at its natural width.
  for (const measureRow of cell.querySelectorAll<HTMLElement>(`[data-chip-measure-row]`)) {
    const inset = measureRow.getBoundingClientRect().left - cellLeft
    content = Math.max(content, measureRow.getBoundingClientRect().width + inset * 2 + CHIP_ADD_AFFORDANCE)
  }

  let longestText = ``
  for (const element of cell.querySelectorAll(`*`)) {
    if (element.children.length > 0) continue
    const text = element.textContent?.trim() ?? ``
    if (text.length > longestText.length) longestText = text
  }

  // Body cells carry their padding on the inner content box, headers carry it on
  // the cell itself, so add whatever this one holds.
  const style = getComputedStyle(cell)
  const padding = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight)
  return { width: content + padding + CONTENT_SLACK, longestText }
}

/**
 * Whether the column prints its value verbatim, checked against the rows that are
 * actually on screen. Where it doesn't (dates rendered as `Aug 21, 2026`, enums
 * rendered as labels, cells built from a custom component) the stored value is not
 * what the reader sees, and measuring it would size the column to the wrong string.
 */
function rendersValuesVerbatim(renderedTexts: string[], values: Set<string>): boolean {
  const meaningful = renderedTexts.filter((text) => text.length > 0)
  return meaningful.length > 0 && meaningful.every((text) => values.has(text))
}

/**
 * The width that fits this column's contents, Excel-style.
 *
 * Rows outside the virtual window have no DOM to measure, so the rendered cells set
 * the floor and a text measurement over every loaded row extends it, guarded by the
 * verbatim check above.
 *
 * Returns `undefined` when the column isn't on screen to be measured.
 */
export function measureAutoFitWidth<T>(table: Table<T>, columnId: string, container: HTMLElement): number | undefined {
  const selector = `[data-column-id="${CSS.escape(columnId)}"]`
  const headerCell = container.querySelector<HTMLElement>(`th${selector}`)
  if (!headerCell) return undefined
  const bodyCells = [...container.querySelectorAll<HTMLElement>(`td${selector}`)]

  const measurements = bodyCells.map(measureCell)
  let width = measureCell(headerCell).width
  for (const measurement of measurements) {
    width = Math.max(width, measurement.width)
  }

  const loadedValues: string[] = []
  for (const row of table.getCoreRowModel().rows) {
    const value = row.getValue(columnId)
    if (typeof value === `string` || typeof value === `number`) loadedValues.push(String(value))
  }

  const renderedTexts = measurements.map((measurement) => measurement.longestText)
  if (loadedValues.length > 0 && rendersValuesVerbatim(renderedTexts, new Set(loadedValues))) {
    const measure = textWidthMeasurer(getComputedStyle(bodyCells[0] ?? headerCell).font)
    const longest = [...loadedValues].sort((a, b) => b.length - a.length).slice(0, LONGEST_VALUES_TO_MEASURE)
    for (const value of longest) {
      width = Math.max(width, measure(value) + TEXT_CELL_GUTTERS)
    }
  }

  return Math.round(Math.min(Math.max(width, MIN_WIDTH), MAX_WIDTH))
}
